import { collection, doc, getDoc, getDocs, setDoc } from 'firebase/firestore';
import { firestore } from '../../shared/config/firebase';
import { LearningQuestion, learningQuestionConverter, OcdInfo, ocdInfoConverter, OcdProfile } from './ocdProfileTypes';

export class OcdProfileService {
  private static instance: OcdProfileService;

  static getInstance(): OcdProfileService {
    if (!OcdProfileService.instance) {
      OcdProfileService.instance = new OcdProfileService();
    }
    return OcdProfileService.instance;
  }

  async getOcdProfile(userId: string): Promise<OcdProfile | null> {
    const ocdProfileRef = collection(firestore, `user_info/${userId}/ocd_profile`).withConverter(ocdInfoConverter);
    const learningQuestionsRef = collection(firestore, `user_info/${userId}/learning_questions`).withConverter(learningQuestionConverter);
    const ocdProfileAction = getDocs(ocdProfileRef);
    const learningQuestionsAction = getDocs(learningQuestionsRef);

    const [ocdProfileDocs, learningQuestionsDocSnaps] = await Promise.all([ocdProfileAction, learningQuestionsAction]);

    if (ocdProfileDocs.empty && learningQuestionsDocSnaps.empty) return null;

    const ocdProfileData = new Map(
      ocdProfileDocs.docs.map((doc) => {
        const data = doc.data();
        return [doc.id, data];
      })
    );

    const learningQuestionsData = learningQuestionsDocSnaps.docs.map((doc) => doc.data());

    const ocdProfile: OcdProfile = {
      ocdInfos: Object.fromEntries(ocdProfileData),
      learningQuestions: learningQuestionsData,
    };

    return ocdProfile;
  }

  async saveOcdTypes(userId: string, ocdTypes: OcdInfo): Promise<void> {
    await this.saveOcdInfo(userId, ocdTypes, 'ocd_types');
  }

  async saveWhatBringsYou(userId: string, whatBringsYou: OcdInfo): Promise<void> {
    await this.saveOcdInfo(userId, whatBringsYou, 'what_brings_you');
  }

  async saveCoreFears(userId: string, coreFears: OcdInfo): Promise<void> {
    await this.saveOcdInfo(userId, coreFears, 'core_fears');
  }

  public async saveOcdInfo(userId: string, ocdInfo: OcdInfo, ocdInfoType: string): Promise<void> {
    const ocdProfileRef = doc(firestore, `user_info/${userId}/ocd_profile/${ocdInfoType}`).withConverter(ocdInfoConverter);
    await setDoc(ocdProfileRef, ocdInfo);
  }

  async saveLearningQuestion(userId: string, learningQuestion: LearningQuestion): Promise<LearningQuestion | undefined> {
    const docRef = doc(firestore, `user_info/${userId}/learning_questions/${learningQuestion.id}`).withConverter(learningQuestionConverter);
    await setDoc(docRef, learningQuestion, { merge: true });
    const docSnap = await getDoc(docRef);
    return docSnap.data();
  }
}

export const ocdProfileService = OcdProfileService.getInstance();
